import { OfferContainerProps as RefinanceContainerProps, Identifier, UserInfo } from '@types';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { auth, config as publicConfig } from '../config';
import React from 'react';
import { AuthenticatedLeadForms, AuthenticatedLeadFormsProps, EventType, FormatWithMask, mobileMask } from 'shared-ui-components';
import { selectLoan, selectUserInfo } from '@store/selectors/widgets.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { FormWrapper } from '@app.styled';
import { broadcastCashOutResponse } from '@store/actions/widgets.actions';
import { CodingConstants } from '@constants';
import { addressFormat } from '@utils/addressFormat';
import FullAppContainer from '@containers/FullAppContainer';
import { get } from 'lodash';

export const RefiContainer: React.FC<RefinanceContainerProps> = ({ loanNumber, updateLoanType, offerDetails, flags }) => {
    const dispatch = useDispatch();
    const token = auth.getOkta().getAccessToken();
    const userInfo: UserInfo = useSelector(selectUserInfo);
    const loanList: Identifier[] = useSelector(selectLoan);
    const selectedLoan = loanList.find(loanlist => loanlist.identifier === loanNumber);
    const { address1, address2, city, state, zip } = selectedLoan.propertyAddress;
    const { email, firstName, lastName, mobile } = userInfo;
    const config: any = {
        metadata: {
            steps: 1,
            type: 'AuthRefi',
            keyword: 'Refinance'
        },
        1: {
            screen: 'ReviewInformation',
            metadata: {
                id: 'cashout-review-information',
                title: 'Review and Submit Information',
                navigation: {
                    current: 1
                },
                metadata: {
                    reviewInformationList: [
                        {
                            id: 1,
                            title: 'Refinance Type',
                            caption: ['Refinance and Get Cash'],
                            isEdit: false,
                            stepNumber: 0,
                            valueKey: ['refinanceType']
                        },
                        {
                            id: 2,
                            title: 'Property Information',
                            caption: ['12000 Government Center Parkway Fairfax, VA 22035'],
                            isEdit: false,
                            stepNumber: 1,
                            valueKey: ['propertyInformation']
                        },
                        {
                            id: 3,
                            title: 'Contact Information',
                            caption: [
                                `John Smith`,
                                'john.smith@gmail.com',
                                '212-555-9999',
                                '12000 Government Center Parkway, 202, Fairfax, VA, 22035'
                            ],
                            isEdit: true,
                            stepNumber: 2,
                            valueKey: ['fullName', 'email', 'formattedPhoneNumber', 'propertyInformation']
                        }
                    ]
                }
            }
        },
        2: {
            screen: 'ContactInformation',
            metadata: {
                navigation: {
                    current: 2,
                    back: 1,
                    next: 1
                }
            }
        }
    };
    const refinanceProps: AuthenticatedLeadFormsProps = {
        config,
        accessToken: token,
        logApiEvent: (event: EventType) => {
            logApiEvent({
                apiName: event.apiName,
                action: event.action,
                correlationId: event.correlationId,
                timestamp: event.timestamp,
                loanNumber: event.loanNumber,
                description: event.description,
                type: 'Refinance widget'
            });
        },
        logUserEvent: (event: EventType) => {
            logUserEvent({
                type: 'Refinance widget',
                action: event.action,
                loanNumber: event.loanNumber,
                description: event.description
            });
        },

        metadata: {
            initialState: {
                0: { refinanceType: 'Refinance' },
                1: { propertyInformation: addressFormat(selectedLoan.propertyAddress) },
                2: {
                    fullName: `${firstName} ${lastName}`,
                    formattedPhoneNumber: FormatWithMask({
                        mask: mobileMask,
                        text: mobile
                    }).masked,
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    phoneNumber: mobile,
                    propertyInformation: addressFormat(selectedLoan.propertyAddress),
                    address: {
                        street: address1,
                        street2: address2,
                        city: city,
                        state: state,
                        zipCode: zip
                    }
                }
            },
            request: {
                offerGUID: offerDetails.offerGUID,
                borrowers: [
                    {
                        address: {
                            street: address1,
                            street2: address2,
                            city: city,
                            state: state,
                            zipCode: zip
                        },
                        firstName: firstName,
                        middleName: '',
                        lastName: lastName,
                        nameSuffix: '',
                        email: email,
                        phone: mobile
                    }
                ],
                loanNumber: loanNumber,
                productInfo: {
                    type: 'refinance',
                    refinanceDetails: {
                        type: offerDetails.product_Campaign.toUpperCase()
                    }
                },
                referralSource: 'ACP',
                utmDetails: {
                    utmDevice: 'Web',
                    utmDevicemodel: 'Chrome'
                }
            }
        },
        leadApiParams: {
            baseUrl: publicConfig.env('leadSubmissionUrl'),
            xApiKey: publicConfig.env('xApiKey')
        },
        onComplete: (metadata: any) => {
            updateLoanType(CodingConstants.servicingLoanType);
            dispatch(broadcastCashOutResponse(metadata));
        }
    };

    if (offerDetails.product_Campaign === 'SL') {
        refinanceProps.metadata.request.productInfo.refinanceDetails.streamLineDetails = {};
    }

    if (offerDetails.product_Campaign === 'RT') {
        refinanceProps.metadata.request.productInfo.refinanceDetails.rateTermDetails = {};
    }

    return (
        <FormWrapper>
            <AuthenticatedLeadForms {...refinanceProps} />
        </FormWrapper>
    );
};
